/**
 * Auth Store — Zustand-powered centralized authentication state.
 * Replaces scattered localStorage reads/writes and duplicated session logic.
 *
 * All token + user persistence is handled here; components subscribe to
 * reactive state instead of touching localStorage directly.
 */
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import apiClient from './apiClient';
import type { PlanId, UsageState } from './subscription';

export interface UserData {
  id?: string;
  name: string;
  email: string;
  joinedAt: string;
  plan?: PlanId;
  usage?: UsageState;
  profilePicture?: string;
  // Profile fields
  bio?: string;
  location?: string;
  website?: string;
  banner?: string;
  skills?: string[];
  githubUsername?: string;
  socials?: Record<string, string>;
  phone?: string;
  workEmail?: string;
  isVerified?: boolean;
  projects?: Array<{
    id: string;
    name: string;
    description: string;
    icon?: string;
    tags?: string[];
    url?: string;
  }>;
}

interface AuthState {
  /** The current user (null = logged out) */
  user: UserData | null;
  /** JWT or demo-key string */
  token: string | null;
  /** Convenience derived flag */
  isAuthenticated: boolean;
  /** True while the initial session hydration is running */
  isHydrating: boolean;
  /** Profile picture URL (if set) */
  profilePicture: string | null;

  // ── Actions ──
  /** Populate store from localStorage + verify token against backend */
  hydrateSession: () => Promise<void>;
  /** Set user after successful auth */
  login: (user: UserData, token: string) => void;
  /** Clear everything */
  logout: () => void;
    /** Partial user update (merges with current user) */
    setUser: (user: Partial<UserData>) => void;
  /** Update profile fields (bio, location, website, skills, etc.) */
  updateProfileFields: (fields: Partial<UserData>) => Promise<{ success: boolean; error?: string }>;
  /** Submit verification request (phone + work email) */
  submitVerification: (phone: string, workEmail: string) => Promise<boolean>;
  /** Update profile picture */
  setProfilePicture: (url: string | null) => void;
  /** Update subscription plan and usage */
  setSubscription: (plan: PlanId, usage: UsageState) => void;
  /** Increment a monthly quota counter optimistically */
  incrementUsage: (feature: 'voiceChat' | 'studioChat') => void;
}

const STORAGE_KEYS = {
  token: 'scrudix_token',
  refreshToken: 'scrudix_refresh_token',
  auth: 'scrudix_auth',
  user: 'scrudix_user',
  picture: 'scrudix_profile_picture',
} as const;

function safeSetItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn(`[localStorage] Failed to write key "${key}":`, e);
    if (key === STORAGE_KEYS.user) {
      try {
        const parsed = JSON.parse(value);
        if (parsed.profilePicture) {
          delete parsed.profilePicture;
          safeSetItem(key, JSON.stringify(parsed));
        }
      } catch { /* ignore */ }
    }
  }
}

/**
 * Normalize a raw user payload (from API or localStorage) into a consistent
 * UserData shape. Defensive normalization prevents dashboard crashes when a
 * field arrives as null/undefined or when a date needs string conversion.
 */
function normalizeUser(raw: any): UserData {
  return {
    id: raw?.id ?? undefined,
    name: String(raw?.name ?? ''),
    email: String(raw?.email ?? ''),
    joinedAt: String(raw?.joinedAt ?? new Date().toISOString()),
    plan: raw?.plan ?? 'free',
    usage: raw?.usage,
    profilePicture: raw?.profilePicture ?? undefined,
    bio: raw?.bio ?? undefined,
    location: raw?.location ?? undefined,
    website: raw?.website ?? undefined,
    banner: raw?.banner ?? undefined,
    skills: Array.isArray(raw?.skills) ? raw.skills : undefined,
    githubUsername: raw?.githubUsername ?? undefined,
    socials: raw?.socials && typeof raw.socials === 'object' ? raw.socials : undefined,
    phone: raw?.phone ?? undefined,
    workEmail: raw?.workEmail ?? undefined,
    isVerified: Boolean(raw?.isVerified),
    projects: Array.isArray(raw?.projects) ? raw.projects : undefined,
  };
}

/** Read persisted user data, if any */
function readPersistedUser(): UserData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.user);
    return raw ? normalizeUser(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    user: readPersistedUser(),
    token: localStorage.getItem(STORAGE_KEYS.token),
    isAuthenticated: localStorage.getItem(STORAGE_KEYS.auth) === 'true',
    isHydrating: true,
    profilePicture: localStorage.getItem(STORAGE_KEYS.picture),

    hydrateSession: async () => {
      set({ isHydrating: true });

      const initialToken = localStorage.getItem(STORAGE_KEYS.token);
      let storedToken = initialToken;
      if (!storedToken && localStorage.getItem(STORAGE_KEYS.auth) === 'true') {
        storedToken = 'demo-key';
        safeSetItem(STORAGE_KEYS.token, storedToken);
      }

      if (!storedToken) {
        set({ user: null, token: null, isAuthenticated: false, isHydrating: false });
        return;
      }

      try {
        const meResponse = await apiClient.getCurrentUser();

        // Guard: if the token in localStorage changed during the async call,
        // a fresh login happened — do NOT clear the new token/session.
        const currentToken = localStorage.getItem(STORAGE_KEYS.token);
        if (currentToken !== storedToken && currentToken !== null) {
          // A fresh login replaced our token — trust the fresh session
          const persistedUser = readPersistedUser();
          if (persistedUser) {
            set({ user: persistedUser, token: currentToken, isAuthenticated: true, isHydrating: false });
          } else {
            set({ isHydrating: false });
          }
          return;
        }

        if (meResponse.success && meResponse.data?.user) {
          const user = normalizeUser(meResponse.data.user);

          // Hydrate subscription state from billing endpoint (resets counters monthly)
          try {
            const billingResponse = await apiClient.getBillingStatus();
            if (billingResponse.success && billingResponse.data) {
              user.plan = billingResponse.data.plan;
              user.usage = billingResponse.data.usage;
            }
          } catch {
            // Leave whatever /me returned
          }

          const profilePicture = user.profilePicture || localStorage.getItem(STORAGE_KEYS.picture);
          if (profilePicture) {
            safeSetItem(STORAGE_KEYS.picture, profilePicture);
          }
          set({ user, token: storedToken, isAuthenticated: true, isHydrating: false, profilePicture: profilePicture || null });
          safeSetItem(STORAGE_KEYS.user, JSON.stringify(user));
          safeSetItem(STORAGE_KEYS.auth, 'true');

          // Fetch remote preferences to sync AI usage percent
          void apiClient.getUserPreferences().then((res) => {
            if (res.success && Array.isArray(res.data)) {
              const usagePref = res.data.find((p) => p.key === 'scrudix_ai_usage_percent');
              if (usagePref) {
                localStorage.setItem(`scrudix_ai_usage_percent_${user.email}`, usagePref.value);
                window.dispatchEvent(new Event('scrudix_usage_updated'));
              }
            }
          }).catch(() => {});
        } else {
          // Distinguish auth rejection from network errors:
          // Only clear session on real auth failures, NOT on "Cannot reach backend"
          const isTransientError =
            meResponse.error?.includes('Cannot reach backend') ||
            meResponse.error?.includes('Network error') ||
            meResponse.error?.includes('temporarily unavailable') ||
            meResponse.error?.includes('try again');

          if (isTransientError) {
            // Backend unreachable — trust persisted session data if we have it
            const persistedUser = readPersistedUser();
            if (persistedUser) {
              const profilePicture = persistedUser.profilePicture || localStorage.getItem(STORAGE_KEYS.picture);
              set({ user: persistedUser, token: storedToken, isAuthenticated: true, isHydrating: false, profilePicture: profilePicture || null });
            } else {
              set({ isHydrating: false });
            }
          } else {
            // Token was explicitly rejected (401) — clear session
            // But only if the token hasn't been replaced by a fresh login
            const latestToken = localStorage.getItem(STORAGE_KEYS.token);
            if (latestToken === storedToken) {
              get().logout();
            }
            set({ isHydrating: false });
          }
        }
      } catch {
        // Network-level failure — preserve session if we have persisted data
        const persistedUser = readPersistedUser();
        if (persistedUser) {
          const profilePicture = persistedUser.profilePicture || localStorage.getItem(STORAGE_KEYS.picture);
          set({ user: persistedUser, token: storedToken, isAuthenticated: true, isHydrating: false, profilePicture: profilePicture || null });
        } else {
          // Only clear if token hasn't been replaced by a fresh login
          const latestToken = localStorage.getItem(STORAGE_KEYS.token);
          if (latestToken === storedToken) {
            get().logout();
          }
          set({ isHydrating: false });
        }
      }
    },

    login: (user, token) => {
      const normalized = normalizeUser(user);
      safeSetItem(STORAGE_KEYS.token, token);
      safeSetItem(STORAGE_KEYS.auth, 'true');
      safeSetItem(STORAGE_KEYS.user, JSON.stringify(normalized));
      if (normalized.profilePicture) {
        safeSetItem(STORAGE_KEYS.picture, normalized.profilePicture);
      } else {
        localStorage.removeItem(STORAGE_KEYS.picture);
      }
      set({ user: normalized, token, isAuthenticated: true, profilePicture: normalized.profilePicture ?? null });
      // Async: hydrate subscription state from billing endpoint
      void apiClient.getBillingStatus().then((res) => {
        if (res.success && res.data) {
          get().setSubscription(res.data.plan, res.data.usage);
        }
      });

      // Sync preferences on login
      void apiClient.getUserPreferences().then((res) => {
        if (res.success && Array.isArray(res.data)) {
          const usagePref = res.data.find((p) => p.key === 'scrudix_ai_usage_percent');
          if (usagePref) {
            localStorage.setItem(`scrudix_ai_usage_percent_${normalized.email}`, usagePref.value);
            window.dispatchEvent(new Event('scrudix_usage_updated'));
          }
        }
      }).catch(() => {});
    },

    logout: () => {
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.refreshToken);
      localStorage.removeItem(STORAGE_KEYS.auth);
      localStorage.removeItem(STORAGE_KEYS.user);
      localStorage.removeItem(STORAGE_KEYS.picture);
      // Clear OAuth/workspace chooser flags so a subsequent login starts clean
      localStorage.removeItem('scrudix_workspace_choice_required');
      localStorage.removeItem('scrudix_oauth_just_connected');
      localStorage.removeItem('scrudix_google_connected');
      localStorage.removeItem('scrudix_github_connected');
      set({ user: null, token: null, isAuthenticated: false, profilePicture: null });
    },

    setUser: (user) => {
      const current = get().user;
      if (!current && (!user.name || !user.email)) {
        // Cannot build a full user without required fields and no existing user.
        return;
      }
      const merged: UserData = current
        ? { ...current, ...user, usage: user.usage ?? current.usage, plan: user.plan ?? current.plan }
        : normalizeUser(user);
      safeSetItem(STORAGE_KEYS.user, JSON.stringify(merged));
      // Keep the standalone profile picture in sync with the user object so every
      // subscriber (Dashboard top bar, Community, etc.) updates automatically.
      const profilePicture = merged.profilePicture ?? null;
      if (profilePicture) {
        safeSetItem(STORAGE_KEYS.picture, profilePicture);
      } else {
        localStorage.removeItem(STORAGE_KEYS.picture);
      }
      set({ user: merged, isAuthenticated: true, profilePicture });
    },

    updateProfileFields: async (fields) => {
      const current = get().user;
      if (!current) return { success: false, error: 'Not authenticated' };

      // Optimistically merge local changes, but do not send profilePicture when
      // updating text fields; otherwise a stale value in `current` can overwrite
      // a picture the user just saved in a separate call.
      const optimistic: UserData = { ...current, ...fields };
      const profilePayload = { ...optimistic };
      delete (profilePayload as Partial<UserData>).profilePicture;

      // Sync to backend first so we can surface errors before updating local state.
      const response = await apiClient.updateProfile(optimistic.name, undefined, profilePayload);
      if (!response.success) {
        return { success: false, error: response.error || 'Failed to update profile' };
      }

      // Trust the server response as the source of truth and broadcast the update
      // through setUser so all subscribers (Dashboard, Community, AccountWorkspace)
      // receive the exact same object.
      const serverUser = response.data?.user;
      const merged: UserData = serverUser
        ? normalizeUser({ ...current, ...serverUser, ...optimistic })
        : optimistic;
      get().setUser(merged);
      return { success: true };
    },

    submitVerification: async (phone, workEmail) => {
      const current = get().user;
      if (!current) return false;
      try {
        const response = await apiClient.verifyProfile({ phone, workEmail });
        if (response.success) {
          const merged: UserData = { ...current, phone, workEmail, isVerified: true };
          safeSetItem(STORAGE_KEYS.user, JSON.stringify(merged));
          set({ user: merged });
          return true;
        }
      } catch {
        // Fall through to local-only verification if backend is unavailable
      }
      // Local-only verification for demo/dev environments
      const merged: UserData = { ...current, phone, workEmail, isVerified: true };
      safeSetItem(STORAGE_KEYS.user, JSON.stringify(merged));
      set({ user: merged });
      return true;
    },

    setProfilePicture: (url) => {
      if (url) {
        safeSetItem(STORAGE_KEYS.picture, url);
      } else {
        localStorage.removeItem(STORAGE_KEYS.picture);
      }
      set({ profilePicture: url });
    },

    setSubscription: (plan, usage) => {
      const current = get().user;
      if (!current) return;
      const merged: UserData = { ...current, plan, usage };
      safeSetItem(STORAGE_KEYS.user, JSON.stringify(merged));
      set({ user: merged });
    },

    incrementUsage: (feature) => {
      const current = get().user;
      if (!current?.usage) return;
      const usage: UsageState = { ...current.usage };
      const currentMonth = new Date().toISOString().slice(0, 7);
      if (usage.usageMonth !== currentMonth) {
        usage.voiceChatsThisMonth = 0;
        usage.studioChatsThisMonth = 0;
        usage.queriesThisMonth = 0;
        usage.usageMonth = currentMonth;
      }
      if (feature === 'voiceChat') usage.voiceChatsThisMonth += 1;
      if (feature === 'studioChat') usage.studioChatsThisMonth += 1;
      const merged: UserData = { ...current, usage };
      safeSetItem(STORAGE_KEYS.user, JSON.stringify(merged));
      set({ user: merged });
    },
  }))
);
